import EventBus from '/@/modules/core/app/helpers/EventBus';
import { apiAxios } from '/@/modules/core/app/helpers/AxiosHelper';
import { BroadcastChannel, Route } from '/@/modules/core/app/constants/api.constants';
import PusherHelper from '/@/integrations/pusher/PusherHelper';
import { NotificationBroadcast } from '/@/modules/notification/models/NotificationBroadcast';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import Echo, { Channel as EchoChannel } from 'laravel-echo';
import { AxiosResponse } from 'axios';
import { Channel } from 'pusher-js';
import { BaseSocketEvent } from '/@/modules/core/app/events/BaseSocketEvent';
import { commits } from '@/modules/core/app/helpers/store';
import { Env } from '@/Env';

// Provided by laravel-echo
const NOTIFICATION_EVENT: string =
  '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

export class BroadcastService {
  authUser?: AuthUser;

  echo?: Echo | null;

  pusherHelper?: typeof PusherHelper;

  constructor() {
    this.pusherHelper = PusherHelper;
  }

  init(user: AuthUser): void {
    if (!user) {
      return;
    }

    this.pusherHelper?.setLogToConsole(false);

    this.authUser = user;
    this.echo =
      this.echo ??
      this.pusherHelper?.getEchoInstance({
        broadcaster: 'pusher',
        // @ts-ignore
        key: Env.nodeEnv().VITE_APP_PUSHER_CLIENT_KEY,
        // @ts-ignore
        cluster: Env.nodeEnv().VITE_APP_PUSHER_CLUSTER,
        forceTLS: true,
        authorizer: this.authorize()
      });
    commits.auth.setEchoInstance(this.echo);
  }

  authorize(): (channel: Channel) => void {
    return (
      channel: Channel
    ): {
      authorize: (
        socketId: number,
        callback: (value: boolean, response: AxiosResponse | unknown) => void
      ) => Promise<void>;
    } => ({
      authorize: async (
        socketId: number,
        callback: (value: boolean, response: AxiosResponse | unknown) => void
      ): Promise<void> => {
        try {
          const response: AxiosResponse = await apiAxios.post(Route.BROADCASTING_AUTH, {
            socket_id: socketId,
            channel_name: channel.name
          });
          callback(false, response);
        } catch (e) {
          callback(true, e);
        }
      }
    });
  }

  destroy(): void {
    this.echo?.disconnect();
    this.echo = null;
    commits.auth.setEchoInstance(undefined);
  }

  listen(
    eventClass: typeof BaseSocketEvent,
    callback: ((data: BaseSocketEvent) => void) | undefined = undefined,
    identifier: number | string | undefined = undefined
  ): EchoChannel | undefined {
    const channelName: string | null = eventClass.getChannelName(this.authUser, identifier);
    if (!channelName) {
      return undefined;
    }

    return this.getEchoChannel(channelName)?.listen(
      eventClass.getEchoEventName(),
      this.getCallback(callback, eventClass)
    );
  }

  listenForNotifications(
    callback: ((data: NotificationBroadcast) => void) | undefined = undefined
  ): EchoChannel | undefined {
    if (!callback) {
      return undefined;
    }

    return this.getEchoChannel(this.getNotificationChannel())?.notification(
      (payload: NotificationBroadcast) => callback(NotificationBroadcast.mapModel(payload))
    );
  }

  stopListening(
    eventClass: typeof BaseSocketEvent,
    callback: ((data: BaseSocketEvent) => void) | undefined = undefined,
    identifier: number | string | undefined = undefined
  ): EchoChannel | undefined {
    const channelName: string | null = eventClass.getChannelName(this.authUser, identifier);
    if (!channelName) {
      return undefined;
    }

    return this.getEchoChannel(channelName)?.stopListening(eventClass.getEchoEventName(), callback);
  }

  stopListeningForNotifications(): EchoChannel | undefined {
    return this.getEchoChannel(this.getNotificationChannel())?.stopListening(NOTIFICATION_EVENT);
  }

  private getCallback(
    callback: ((data: BaseSocketEvent) => void) | undefined,
    eventClass: typeof BaseSocketEvent
  ): (data: BaseSocketEvent) => void {
    if (callback) {
      return (data: BaseSocketEvent): void => callback(new eventClass(data));
    }

    return (data: BaseSocketEvent): void => {
      EventBus.signal(eventClass.getEventName() as string, new eventClass(data));
    };
  }

  private getEchoChannel(channelName: string): EchoChannel | undefined {
    return channelName === BroadcastChannel.PUBLIC
      ? this.echo?.channel(channelName)
      : this.echo?.private(channelName);
  }

  private getNotificationChannel(): string {
    return `${ModelType.USER}.${this.authUser?.id}`;
  }
}

export default new BroadcastService();
